import { useEffect, useState } from 'react';
import {
  Popconfirm,
  Skeleton,
  Space,
  Switch,
  Tooltip,
  Typography,
  notification,
  theme,
} from 'antd';
import { BankOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useCliente } from 'providers/Clientes/ClienteProvider';
import { useClientesActions } from 'providers/Clientes/ClientesActionsProvider';
import useTenantFlags from 'hooks/useTenantFlags';

const { Text, Title } = Typography;
const { useToken } = theme;

/**
 * Barra compacta de identidad del cliente, persistente arriba de los tabs de
 * la ficha. Da contexto (quién es el cliente) y expone los controles que antes
 * vivían enterrados en el menú ⋯ del listado:
 *  - Estado (active) — Switch con confirmación al desactivar.
 *  - Preventiva automática (automatizadoManPreven) — Switch que reusa el flujo
 *    de useClientesActions (guard de ascensores + modal 409 SIN_PLAN_PARCIAL).
 *
 * La edición de los datos del cliente vive ahora en el tab `general`
 * (ClienteInicioPage), con el formulario inline + botón Guardar.
 */
export default function ClienteHeader({ id }) {
  const { token } = useToken();
  const { data, loading, call } = useCliente();
  const { toggleStatus, toggleAutoPreven } = useClientesActions();
  const { readOnly } = useTenantFlags();
  const [preven, setPreven] = useState(false);

  useEffect(() => {
    call(id);
  }, [call, id]);

  // El Switch de preventiva es optimista (toggleAutoPreven es debounced y maneja
  // su propio modal/errores); se re-sincroniza cada vez que llega `data`.
  useEffect(() => {
    if (data) setPreven(!!data.automatizadoManPreven);
  }, [data]);

  const panelStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: token.marginSM,
    padding: token.paddingMD,
    marginBottom: token.marginSM,
    background: token.colorBgContainer,
    border: `1px solid ${token.colorBorder}`,
    borderRadius: token.borderRadiusLG,
  };

  if (loading && !data) {
    return (
      <div style={panelStyle}>
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: 240 }} />
      </div>
    );
  }
  if (!data) return null;

  const ascCount = data.Ascensores?.length ?? data.nAscensores ?? 0;

  const handleEstado = async () => {
    await toggleStatus(id, data.active);
    await call(id);
  };

  const handlePreven = (checked) => {
    if (checked && ascCount === 0) {
      notification.error({
        message: 'Para activar la preventiva automática el cliente debe tener al menos un ascensor',
      });
      return;
    }
    setPreven(checked);
    toggleAutoPreven(id, checked);
  };

  return (
    <div style={panelStyle}>
      <div style={{ minWidth: 0 }}>
        <Title level={5} style={{ margin: 0 }}>
          <Space size={8}>
            <BankOutlined style={{ color: token.colorPrimary }} />
            {data.name}
          </Space>
        </Title>
        <Text type="secondary">
          {[data.rut, data.commune, `${ascCount} ascensores`].filter(Boolean).join(' · ')}
        </Text>
        {(data.contactName || data.phone) && (
          <>
            <br />
            <Text type="secondary">
              {[data.contactName, data.phone].filter(Boolean).join(' · ')}
            </Text>
          </>
        )}
      </div>

      <Space wrap size="middle">
        {data.active ? (
          <Popconfirm
            title="¿Desactivar este cliente?"
            okText="Sí"
            cancelText="No"
            onConfirm={handleEstado}
            disabled={readOnly}
          >
            <Switch
              checked
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
              disabled={readOnly}
            />
          </Popconfirm>
        ) : (
          <Switch
            checked={false}
            onChange={handleEstado}
            checkedChildren="Activo"
            unCheckedChildren="Inactivo"
            disabled={readOnly}
          />
        )}

        <Tooltip title="Genera solicitudes de mantención preventiva cada mes">
          <Space size={6}>
            <ThunderboltOutlined style={{ color: token.colorWarning }} />
            <Text>Preventiva auto.</Text>
            <Switch checked={preven} onChange={handlePreven} disabled={readOnly} />
          </Space>
        </Tooltip>
      </Space>
    </div>
  );
}
